import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import ReciboVentaDocumento from './ReciboVentaDocumento';

/**
 * Obtiene el recibo del talonario de una Factura o Nota de Venta.
 * Ver nunca consume número: solo si la venta no tiene recibo (ventas anteriores al talonario)
 * se ofrece generarlo, y eso sí usa el próximo número configurado.
 *  - base: '/api/facturacion/facturas' o '/api/facturacion/notas-venta'
 * Devuelve el recibo, o null si no hay (o el usuario no quiso generarlo).
 */
export const obtenerReciboVenta = async (base, id) => {
    let sinRecibo;
    try {
        const { data } = await api.get(`${base}/${id}/recibo/`);
        return data.recibo;
    } catch (e) {
        if (!e.response?.data?.sin_recibo) throw e;
        sinRecibo = e.response.data;
    }
    if (!window.confirm(`Esta venta no tiene recibo.\n\n¿Generarlo ahora con el Nº ${sinRecibo.siguiente_numero} del talonario ${sinRecibo.talonario}?`)) return null;
    const { data } = await api.post(`${base}/${id}/recibo/`, {});
    return data.recibo;
};

/**
 * Modal para ver, corregir e imprimir el Recibo / Detalle de Compra de Vision Click.
 * El recibo se emite junto con su Factura o Nota de Venta; aquí solo se corrige (el número no cambia).
 *
 * Props:
 *  - reciboExistente: Recibo emitido
 *  - onClose: Callback para cerrar modal
 *  - onGuardado: Callback tras guardar
 */
const ModalReciboVenta = ({ reciboExistente, onClose, onGuardado = () => {} }) => {
    const [datos, setDatos] = useState({ ...reciboExistente });

    const [busquedaArmazon, setBusquedaArmazon] = useState('');
    const [catalogoArmazones, setCatalogoArmazones] = useState([]);

    const [guardando, setGuardando] = useState(false);
    const [mensaje, setMensaje] = useState(null);

    // Búsqueda de armazones en catálogo
    useEffect(() => {
        if (!busquedaArmazon.trim()) { setCatalogoArmazones([]); return; }
        const t = setTimeout(() => {
            api.get('/api/facturacion/catalogo/', { params: { q: busquedaArmazon.trim() } })
                .then(r => {
                    const prods = (r.data || []).filter(item => item.tipo === 'Producto');
                    setCatalogoArmazones(prods.slice(0, 6));
                })
                .catch(() => setCatalogoArmazones([]));
        }, 250);
        return () => clearTimeout(t);
    }, [busquedaArmazon]);

    const seleccionarArmazon = (prod) => {
        setDatos(prev => ({ ...prev, armazon: prod.descripcion }));
        setBusquedaArmazon('');
        setCatalogoArmazones([]);
    };

    const actualizarCampo = (campo, valor) => {
        setDatos(prev => ({
            ...prev,
            [campo]: valor
        }));
    };

    const imprimir = () => {
        window.print();
    };

    const guardar = async () => {
        setGuardando(true);
        setMensaje(null);
        try {
            const { data: guardado } = await api.patch(`/api/facturacion/recibos/${datos.id}/`, datos);
            setDatos(prev => ({ ...prev, ...guardado }));
            setMensaje({ ok: true, texto: 'Recibo guardado correctamente.' });
            onGuardado(guardado);
        } catch (err) {
            setMensaje({ ok: false, texto: err.response?.data?.detail || 'Error al guardar el recibo.' });
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            zIndex: 10045,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16
        }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{
                background: '#ffffff',
                borderRadius: 16,
                width: 900,
                maxWidth: '100%',
                maxHeight: '96vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
            }}>
                {/* BARRA SUPERIOR */}
                <div style={{
                    background: '#164e9e',
                    color: '#ffffff',
                    padding: '14px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }} className="no-print">
                    <div style={{ fontWeight: 800, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i className="bi bi-file-earmark-check"></i>
                        <span>DETALLE DE COMPRA / RECIBO (Talonario Físico)</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button
                            type="button"
                            onClick={imprimir}
                            style={{
                                padding: '6px 14px',
                                borderRadius: 8,
                                border: 'none',
                                background: '#ffffff',
                                color: '#164e9e',
                                fontWeight: 800,
                                fontSize: '0.86rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6
                            }}
                        >
                            <i className="bi bi-printer"></i>
                            Imprimir Recibo
                        </button>

                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#fff',
                                fontSize: '1.6rem',
                                cursor: 'pointer',
                                lineHeight: 1
                            }}
                        >&times;</button>
                    </div>
                </div>

                {/* CONTENIDO SCROLLEABLE */}
                <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, background: '#f8fafc' }}>
                    {/* Corrección del armazón desde la bodega */}
                    <div style={{
                        background: '#ffffff',
                        borderRadius: 12,
                        padding: '12px 16px',
                        border: '1px solid #e2e8f0',
                        marginBottom: 16
                    }} className="no-print">
                        <div style={{ position: 'relative' }}>
                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 800, color: '#475569', marginBottom: 4 }}>
                                <i className="bi bi-eyeglasses me-1"></i>
                                Buscar Armazón en Bodega / Inventario:
                            </label>
                            <input
                                type="text"
                                value={busquedaArmazon}
                                onChange={e => setBusquedaArmazon(e.target.value)}
                                placeholder="Buscar por marca, modelo o color..."
                                style={{
                                    width: '100%',
                                    padding: '8px 10px',
                                    border: '1.5px solid #cbd5e1',
                                    borderRadius: 8,
                                    fontSize: '0.86rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                            {catalogoArmazones.length > 0 && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    background: '#fff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: 8,
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                    zIndex: 30,
                                    maxHeight: 180,
                                    overflowY: 'auto'
                                }}>
                                    {catalogoArmazones.map((pr, k) => (
                                        <div
                                            key={k}
                                            onClick={() => seleccionarArmazon(pr)}
                                            style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '0.84rem', display: 'flex', justifyContent: 'space-between' }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
                                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                        >
                                            <span><strong>{pr.descripcion}</strong> {pr.stock !== undefined && <span style={{ color: '#64748b' }}>(Stock: {pr.stock})</span>}</span>
                                            <span style={{ fontWeight: 800, color: '#164e9e' }}>${Number(pr.precio).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {mensaje && (
                        <div style={{
                            padding: '10px 14px',
                            borderRadius: 8,
                            fontSize: '0.86rem',
                            fontWeight: 600,
                            marginBottom: 14,
                            background: mensaje.ok ? '#ecfdf5' : '#fef2f2',
                            color: mensaje.ok ? '#065f46' : '#991b1b',
                            border: `1px solid ${mensaje.ok ? '#a7f3d0' : '#fecaca'}`
                        }} className="no-print">
                            {mensaje.texto}
                        </div>
                    )}

                    {/* DOCUMENTO EXACTO AL TALONARIO */}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <ReciboVentaDocumento
                            datos={datos}
                            editable={true}
                            onChange={actualizarCampo}
                        />
                    </div>
                </div>

                {/* BARRA INFERIOR DE ACCIONES */}
                <div style={{
                    padding: '12px 20px',
                    borderTop: '1px solid #e2e8f0',
                    background: '#ffffff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }} className="no-print">
                    <div style={{ fontSize: '0.84rem', color: '#64748b' }}>
                        <span>Recibo <strong>{datos.numero}</strong> registrado. Puede modificar cualquier campo o casilla antes de imprimir.</span>
                    </div>

                    <div style={{ display: 'flex', gap: 10 }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                padding: '8px 16px',
                                borderRadius: 8,
                                border: '1.5px solid #cbd5e1',
                                background: '#fff',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            Cerrar
                        </button>

                        <button
                            type="button"
                            onClick={guardar}
                            disabled={guardando}
                            style={{
                                padding: '8px 18px',
                                borderRadius: 8,
                                border: 'none',
                                background: '#059669',
                                color: '#fff',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6
                            }}
                        >
                            <i className="bi bi-save"></i>
                            {guardando ? 'Guardando...' : 'Guardar Cambios'}
                        </button>

                        <button
                            type="button"
                            onClick={imprimir}
                            style={{
                                padding: '8px 18px',
                                borderRadius: 8,
                                border: 'none',
                                background: '#164e9e',
                                color: '#fff',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                boxShadow: '0 2px 6px rgba(22, 78, 158, 0.25)'
                            }}
                        >
                            <i className="bi bi-printer-fill"></i>
                            Imprimir Recibo Oficial
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalReciboVenta;
